// frontend/src/components/EmotionSphere.js
import React, { useState, useRef, useEffect, useCallback, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Billboard, Text, Sphere, Environment } from "@react-three/drei";
import { useAuth } from "../context/AuthContext";
import { WoodMaterial } from "./materials/WoodMaterial";
import { useSphereTouchControls } from "../hooks/useSphereTouchControls";
import { useI18n } from "../i18n";
import "./EmotionSphere.css";

function computeFontSize(text = "", maxSize = 0.25, _minSize = 0.12) {
  const str = text || '';
  if (str.length === 0) return maxSize;

  const words = str.split(' ');
  const maxWordLen = Math.max(...words.map(w => w.length));
  const totalLen = str.length;

  // Tight constraints for 70-char text in EmotionSphere (Radius 2.5)
  const MAX_WIDTH = 2.1;
  const MAX_HEIGHT = 1.8;
  const CHAR_WIDTH_RATIO = 0.55;
  const LINE_HEIGHT_FACTOR = 1.2;

  const sizeByWidth = MAX_WIDTH / (maxWordLen * CHAR_WIDTH_RATIO);
  const sizeByArea = Math.sqrt((MAX_HEIGHT * MAX_WIDTH) / (totalLen * CHAR_WIDTH_RATIO * LINE_HEIGHT_FACTOR));

  const calculated = Math.min(sizeByWidth, sizeByArea);
  const safeSize = calculated * 0.85;

  return Math.min(maxSize, Math.max(safeSize, _minSize));
}

function useIsDarkMode() {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const check = () => {
       const val = document.documentElement.getAttribute('data-theme') === 'dark';
       setIsDark(val);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

const SpinningSphere = ({ radius, isLocked, onClick, onDoubleClick, mode, woodStyle, rotating }) => {
  const meshRef = useRef();
  const isDark = useIsDarkMode();

  useFrame(({ clock }, delta) => {
    if (meshRef.current && !isLocked) {
      // 1. Latido (Vida)
      const t = clock.getElapsedTime();
      const scale = 1 + Math.sin(t * 1.5) * 0.015;
      meshRef.current.scale.set(scale, scale, scale);

      // 2. Rotación
      if (rotating) {
        // ACTIVA: Giro Vertical (Eje X) estilo Rolodex
        meshRef.current.rotation.x += delta * 2.5;
      } else {
        // IDLE: Flotación suave
        meshRef.current.rotation.x += delta * 0.05;
        meshRef.current.rotation.y += delta * 0.02;
      }
    }
  });

  // Material helpers
  const getMaterial = () => {
    if (mode === 'classic') {
       return (
         <meshStandardMaterial
            color={isDark ? "#5c6bc0" : "#7986cb"}
            roughness={0.4}
            metalness={0.6}
            transparent
            opacity={0.8}
            wireframe={false}
         />
       );
    }
    if (mode === 'wood') {
       return null;
    }
    // Jade Stone
    return (
      <meshPhysicalMaterial
        color="#355E3B"
        roughness={0.15}
        metalness={0.05}
        clearcoat={1.0}
        clearcoatRoughness={0.1}
      />
    );
  };

  return (
    <Sphere
      ref={meshRef}
      args={[radius, 64, 64]}
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick && onDoubleClick(); }}
    >
      {mode === 'wood' ? <WoodMaterial type={woodStyle} /> : getMaterial()}
    </Sphere>
  );
};

const SpinnerOverlay = ({ label = 'Cargando...' }) => (
  <div style={{
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.5)', zIndex: 40, flexDirection: 'column', gap: 8, backdropFilter: 'blur(2px)'
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 16, border: '4px solid rgba(0,0,0,0.1)',
      borderTopColor: '#5c6bc0', animation: 'spin 1s linear infinite'
    }} />
    <div style={{ fontSize: 12, color: 'var(--text-color)' }}>{label}</div>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const EmotionSphere = ({ options = [], radius = 2.5, onSelect, onActiveIndexChange, disabled = false, loading = false, rotating = false, initialIndex = 0, onUserInteract }) => {
  const containerRef = useRef(null);
  const [containerNode, setContainerNode] = useState(null);
  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    setContainerNode(node);
  }, []);
  const { t } = useI18n();
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const isDark = useIsDarkMode();

  useEffect(() => {
    if (onActiveIndexChange) {
      onActiveIndexChange(activeIndex);
    }
  }, [activeIndex, onActiveIndexChange]);

  const { user, woodStyle } = useAuth();
  const mode = user?.sphereMode || 'classic';

  const optionsString = JSON.stringify(options);
  useEffect(() => {
    setActiveIndex(initialIndex);
    setSelectedIndex(null);
  }, [optionsString, initialIndex]);

  // Sync activeIndex with initialIndex changes from parent (Agent Q)
  useEffect(() => {
    setActiveIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (disabled || !options.length) return;
    const handleWheel = (e) => {
      if (selectedIndex !== null) return;
      e.preventDefault();
      if (onUserInteract) onUserInteract();
      setActiveIndex((prev) =>
        e.deltaY > 0
          ? (prev < options.length - 1 ? prev + 1 : 0)
          : (prev > 0 ? prev - 1 : options.length - 1)
      );
    };
    const container = containerRef.current;
    container?.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container?.removeEventListener("wheel", handleWheel);
    };
  }, [options.length, disabled, selectedIndex, onUserInteract]);

  const normalizedOptions = useMemo(
    () => options.map(o => ({ ...o, content: o.content ?? o.label ?? String(o.value ?? '') })),
    [options]
  );
  const activeOption = normalizedOptions[activeIndex] || {};

  const handleInteraction = useCallback((type) => {
    if (disabled || loading) return;
    if (onUserInteract) onUserInteract();

    if (type === 'dblclick') {
        if (selectedIndex === activeIndex) {
            setSelectedIndex(null);
            try { onSelect?.(null); } catch (_) {}
        } else {
            setSelectedIndex(activeIndex);
            try { onSelect?.(normalizedOptions[activeIndex].content ?? ''); } catch (_) {}
        }
    }
    // Single click does nothing in EmotionSphere except maybe ensure focus/rotation stop if desired?
    // Current behavior request says: Double click to lock.
  }, [activeIndex, disabled, loading, normalizedOptions, onSelect, selectedIndex, onUserInteract]);

  const handleDoubleTap = useCallback(() => {
    handleInteraction('dblclick');
  }, [handleInteraction]);

  useSphereTouchControls(containerRef, options.length, disabled, selectedIndex, setActiveIndex, handleDoubleTap);

  if (!options.length) {
    if (loading) {
      return (
        <div style={{ position: 'relative', height: 220 }}>
          <SpinnerOverlay label={t('consciousness.loadingOptions')} />
        </div>
      );
    }
    return null;
  }

  const fontSize = computeFontSize(
    activeOption.content,
    activeOption.fontSizeActive || 0.25,
    activeOption.fontSizeInactive || 0.15
  );

  const handleLockOverlayClick = () => {
    if (selectedIndex !== null) {
      setSelectedIndex(null);
      try { onSelect?.(null); } catch (_) {}
    }
  };

  const textColor = selectedIndex === activeIndex
    ? "#ffd700"
    : (mode === 'glass' || mode === 'wood' ? "#FFFFFF" : (isDark ? "#ffffff" : "#1a237e"));

  // Calculate camera distance based on radius to match SphereMenu's "zoom" factor (2.6x)
  const cameraZ = radius * 2.6;

  return (
    <div className={`emotion-sphere-container sphere-${mode} ${selectedIndex !== null ? 'sphere-locked' : ''}`} ref={setContainerRef} style={{ position: 'relative' }}>
      {loading && <SpinnerOverlay label={t('common.loading')} />}
      {containerNode && <Canvas
        camera={{ fov: 50, position: [0, 0, cameraZ] }}
        style={{ height: "100%", background: "transparent", pointerEvents: "auto" }}
        onDoubleClick={(e) => { e.preventDefault(); }}
      >
        <ambientLight intensity={isDark ? 0.6 : 1.0} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <Suspense fallback={null}>
          <Environment files="/potsdamer_platz_1k.hdr" />
        </Suspense>

        <SpinningSphere
            radius={radius}
            mode={mode}
            woodStyle={woodStyle}
            isLocked={selectedIndex !== null}
            onClick={() => handleInteraction('click')}
            onDoubleClick={() => handleInteraction('dblclick')}
            rotating={rotating}
        />

        <group position={[0, 0, radius + 0.2]}>
          <Billboard>
            <Text
              fontSize={fontSize}
              color={textColor}
              anchorX="center"
              anchorY="middle"
              maxWidth={2.1} // Increased to match MAX_WIDTH calculation
              textAlign="center"
              onClick={(e) => { e.stopPropagation(); handleInteraction('click'); }}
              onDoubleClick={(e) => { e.stopPropagation(); handleInteraction('dblclick'); }}
              style={{ cursor: (disabled || loading) ? "default" : "pointer" }}
            >
              {activeOption.content || ""}
            </Text>
          </Billboard>
        </group>
      </Canvas>}

      {/* Overlay UI - Candado dinámico (Top Center) */}
      {selectedIndex !== null && !loading && !disabled && (
        <div
          className="pointer-events-none"
          style={{
            zIndex: 10,
            position: 'absolute',
            top: '12%',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              background: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: '22px',
              height: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(2px)',
              boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              pointerEvents: 'auto'
            }}
            data-sphere-touch-ignore
            onClick={handleLockOverlayClick}
            title={t('consciousness.unlockClick')}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
        </div>
      )}

      <div className="emotion-sphere-dots">
        {normalizedOptions.map((opt, i) => (
          <div
            key={i}
            role="button"
            data-option-text={opt.content}
            className={`dot ${i === activeIndex ? "active" : ""} ${i === selectedIndex ? "selected" : ""}`}
            onClick={() => {
              if (disabled || loading) return;
              if (onUserInteract) onUserInteract();
              if (selectedIndex === i) {
                setSelectedIndex(null);
                try { onSelect?.(null); } catch (_) {}
                return;
              }
              setActiveIndex(i);
              // Para el agente y mejor UX, el click en el punto también selecciona
              setSelectedIndex(i);
              try { onSelect?.(opt.content ?? ''); } catch (_) {}
            }}
            title={opt.content}
          />
        ))}
      </div>

      {/* Hint text for locking */}
      {selectedIndex === null && !loading && !disabled && (
        <div style={{
            textAlign: 'center',
            fontSize: '0.8rem',
            color: 'var(--text-secondary, #888)',
            marginTop: '8px',
            pointerEvents: 'none',
            userSelect: 'none'
        }}>
          {t('consciousness.doubleClickLock')}
        </div>
      )}
    </div>
  );
};

export default EmotionSphere;
